"use strict"

import { scaleLinear, scaleTime } from "d3-scale"

import { getCurrentTime, isNumber, stringifyDate, parseDate, adjustDateValue, convertTZ } from "../../utils"

const MILLIS_PER_DAY = 24 * 3600 * 1e3

class AbstractTimeExtractor {
  min(a, b) {
    const delta = this.difference(a, b)
    if (delta < 0) {
      return a
    } else {
      return b
    }
  }

  max(a, b) {
    const delta = this.difference(a, b)
    if (delta < 0) {
      return b
    } else {
      return a
    }
  }
}

// Padding to add to timescale X-Axis upper range to give padding btw edge of axis and data points
const minutes = 30

export class TimestampExtractor extends AbstractTimeExtractor {
  name = "Timestamp"
  longLabels = true

  getOriginTime(inoculationTime, alternative, live = false) {
    return live ? alternative || inoculationTime : inoculationTime || alternative
  }

  getObservationTime(obs) {
    return obs.timestamp
  }

  getSampleTime(sample) {
    return sample.taken
  }

  getActionTime(action, tzString = null) {
    return action.timestamp
  }

  difference(a, b) {
    if (a && b) {
      return (a.valueOf() - b.valueOf()) / MILLIS_PER_DAY
    } else {
      return 0
    }
  }

  offsetDays(base, offset) {
    if (base) {
      return parseDate(base.valueOf() + offset * MILLIS_PER_DAY)
    } else {
      return getCurrentTime()
    }
  }

  buildAxis(minTime, maxTime, minPos, maxPos) {
    // Give a bit of a buffer above the calc'd range so edge cases don't fall right on the extreme right edge of
    // the graph. Makes it easier to see on plot itself. Does not overwrite zoom ranges or set domains.
    // Adding 30 minutes to maxTime

    let paddedMaxTime
    if (maxTime) {
      try {
        paddedMaxTime = parseDate(maxTime.valueOf()) // calling .valueOf() here to make a copy of the date obj and not modify the original directly
      } catch (error) {
        if (typeof maxTime === "number") {
          paddedMaxTime = parseDate(maxTime)
        }
      }

      paddedMaxTime = paddedMaxTime ? adjustDateValue(paddedMaxTime, "minute", minutes, true) : null
    }

    return scaleTime()
      .domain([minTime, paddedMaxTime || maxTime])
      .range([minPos, maxPos])
  }

  formatter(time) {
    return stringifyDate(time, false)
  }

  verboseFormatter(time) {
    return stringifyDate(time)
  }
}

export class DayExtractor extends AbstractTimeExtractor {
  name = "Day"
  longLabels = false

  getOriginTime(inoculationTime, alternative, live = false) {
    return live ? alternative : 0
  }

  getObservationTime(obs) {
    return obs.day
  }

  getSampleTime(sample) {
    return sample.day
  }

  getActionTime(act, _) {
    return act.day
  }

  difference(a, b) {
    return a - b
  }

  offsetDays(base, offset) {
    return base + offset
  }

  buildAxis(minTime, maxTime, minPos, maxPos) {
    // Give a bit of a buffer above the calc'd range so edge cases don't fall right on the extreme right edge of
    // the graph. Makes it easier to see on plot itself. Does not overwrite zoom ranges or set domains.
    // Adding 30 minutes to maxTime
    let paddedMaxTime

    if (maxTime && typeof maxTime === "number") {
      paddedMaxTime = maxTime + minutes / 1440
    }

    return scaleLinear()
      .domain([minTime, paddedMaxTime || maxTime])
      .range([minPos, maxPos])
  }

  formatter(time) {
    return isNumber(time) ? time.toString() : ""
  }

  verboseFormatter(time) {
    return `Day ${isNumber(time) ? time.toFixed(3).toString() : ""}`
  }
}

const TIME_EXTRACTORS = [new DayExtractor(), new TimestampExtractor()]

export default TIME_EXTRACTORS
